import { cn } from '@/lib/utils';

/**
 * The Factit marks, drawn after the TCIT Suite system in the TCIT brand manual
 * (2026 edition, §3): the product name in capitals, monoline with round
 * terminals, ink-coloured, plus the suite's green rounded square at the top
 * right. The square is the constant that makes every suite product read as
 * one family; the name is never coloured.
 *
 * Masters live in `brand/factit/` at the repository root; the geometry here is
 * the same, so change both together.
 *
 * Strokes are `currentColor` so the caller picks the ink with a `text-*`
 * utility (`text-fg` on light surfaces, white on dark). The square reads the
 * brand token directly: `text-brand` deliberately does not compile (see
 * `theme.css`), and the square is a fill, which is exactly the use the brand
 * green is allowed.
 */

const STROKE = 17;
const BRAND_FILL = { fill: 'var(--semantic-color-brand-primary)' } as const;

interface MarkProps {
  className?: string;
  /** Accessible name. Pass `null` when a visible label already names it. */
  title?: string | null;
}

function a11y(title: string | null) {
  return title === null
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': title } as const);
}

/** «FACTIT» plus the suite square. 4.08 : 1. */
export function FactitWordmark({
  className,
  title = 'Factit',
}: MarkProps): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 408 100"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('h-5 w-auto', className)}
      {...a11y(title)}
    >
      <g stroke="currentColor" strokeWidth={STROKE}>
        <path d="M8.5 91.5V8.5H57.5M31 53H43" />
        <path d="M66.5 91.5L102 8.5L137.5 91.5" />
        <path d="M229.8 23.3A41.5 41.5 0 1 0 229.8 76.7" />
        <path d="M255.8 8.5H286.8M271.3 8.5V91.5" />
        <path d="M315.8 8.5V91.5" />
        <path d="M344.8 8.5H375.8M360.3 8.5V91.5" />
      </g>
      <rect x="391" y="0" width="17" height="17" rx="4" style={BRAND_FILL} />
    </svg>
  );
}

/**
 * The monogram: the initial plus the same square. For the collapsed sidebar,
 * where the wordmark has no room. The manual only allows it where the product
 * is already identified, which inside the app it always is, and never below
 * 28px tall on screen (§2.5), hence `size-7`.
 */
export function FactitMonogram({
  className,
  title = 'Factit',
}: MarkProps): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 90 100"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('size-7', className)}
      {...a11y(title)}
    >
      <path
        d="M8.5 91.5V8.5H57.5M31 53H43"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <rect x="73" y="0" width="17" height="17" rx="4" style={BRAND_FILL} />
    </svg>
  );
}
